import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import MediaLibraryModal from "../../components/CustomComponents/ImageUpload/MediaLibraryModal";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import {
  insertTokenAtCursor,
  resolveMessageWithSamples,
  resolveMediaUrl,
  mergeEmailPresetPlaceholders,
} from "../../utils/marketingTemplateUtils";
import {
  getThemeGradientButtonStyle,
  onThemeGradientButtonEnter,
  onThemeGradientButtonLeave,
} from "../../utils/themeGradientButtonStyle";
import "./emailPresetEditor.scss";

const EMAIL_PRESET_CTA_NONE = "None";
const EMAIL_PRESET_CTA_BOOK_NOW = "BookNow";
const EMAIL_PRESET_CTA_ORDER_NOW = "OrderNow";

const EMAIL_PRESET_CTA_OPTIONS = [
  {
    value: EMAIL_PRESET_CTA_NONE,
    label: "No button",
    hint: "Email ends after your message.",
    icon: "fa-minus-circle",
  },
  {
    value: EMAIL_PRESET_CTA_BOOK_NOW,
    label: "Book Now",
    hint: "Uses your reservation URL.",
    icon: "fa-calendar-alt",
  },
  {
    value: EMAIL_PRESET_CTA_ORDER_NOW,
    label: "Order Now",
    hint: "Uses your menu / ordering URL.",
    icon: "fa-shopping-bag",
  },
];

const normalizeCtaButtonType = (row) => {
  const raw = row?.ctaButtonType ?? row?.CtaButtonType;
  if (raw === EMAIL_PRESET_CTA_BOOK_NOW || raw === EMAIL_PRESET_CTA_ORDER_NOW) {
    return raw;
  }
  if (row?.includeBookNowButton || row?.IncludeBookNowButton) {
    return EMAIL_PRESET_CTA_BOOK_NOW;
  }
  return EMAIL_PRESET_CTA_NONE;
};

const emptyForm = {
  presetTitle: "",
  subject: "",
  htmlBody: "",
  heroImageUrl: "",
  ctaButtonType: EMAIL_PRESET_CTA_NONE,
};

const buildPreviewDocument = (html) => {
  const trimmed = (html || "").trim();
  if (!trimmed) {
    return `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:12px;font-family:Arial,sans-serif;color:#6c757d;">No preview yet.</body></html>`;
  }
  if (/^<!doctype/i.test(trimmed) || /^<html/i.test(trimmed)) {
    return trimmed;
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:Arial,sans-serif;margin:12px;}</style></head><body>${trimmed}</body></html>`;
};

const normalizePreset = (row) => ({
  emailPreset_ID: row?.emailPreset_ID ?? row?.EmailPreset_ID ?? 0,
  emailPreset_Guid: row?.emailPreset_Guid ?? row?.EmailPreset_Guid ?? "",
  presetTitle: row?.presetTitle ?? row?.PresetTitle ?? "",
  subject: row?.subject ?? row?.Subject ?? "",
  htmlBody: row?.htmlBody ?? row?.HtmlBody ?? "",
  heroImageUrl: row?.heroImageUrl ?? row?.HeroImageUrl ?? "",
  ctaButtonType: normalizeCtaButtonType(row),
  isActive: row?.isActive ?? row?.IsActive ?? true,
});

const EmailPresetFormPage = ({ mode }) => {
  const isEdit = mode === "edit";
  const { presetId } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const emailPresetId = isEdit ? Number(presetId) || 0 : 0;

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [emailPresetGuid, setEmailPresetGuid] = useState(() =>
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : ""
  );
  const [isActive, setIsActive] = useState(true);
  const [placeholders, setPlaceholders] = useState([]);
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("visual");
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [marketingSettings, setMarketingSettings] = useState({
    reservationUrl: "",
    menuUrl: "",
  });
  const subjectRef = useRef(null);
  const htmlSourceRef = useRef(null);
  const quillRef = useRef(null);
  const [activeInsertField, setActiveInsertField] = useState("body");
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const openMediaLibrary = useCallback(() => {
    setMediaLibraryOpen(true);
  }, []);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "blockquote", "image"],
          [{ align: [] }],
          [{ color: [] }],
          ["clean"],
        ],
        handlers: {
          image: openMediaLibrary,
        },
      },
    }),
    [openMediaLibrary]
  );

  useEffect(() => {
    if (!CompID) return;

    const loadPlaceholders = async () => {
      try {
        const response = await axiosInstance.get(APIs.GET_EMAIL_PRESET_PLACEHOLDERS, {
          params: { CompID },
        });
        const rows = response.data?.data ?? response.data?.Data ?? [];
        setPlaceholders(mergeEmailPresetPlaceholders(rows));
      } catch {
        setPlaceholders(mergeEmailPresetPlaceholders([]));
      }
    };

    loadPlaceholders();
  }, [CompID]);

  useEffect(() => {
    if (!CompID) return;

    const loadMarketingSettings = async () => {
      try {
        const response = await axiosInstance.get(APIs.GET_MARKETING_SETTINGS, {
          params: { CompID },
        });
        const data = response.data?.data ?? response.data?.Data ?? {};
        setMarketingSettings({
          reservationUrl: data.reservationUrl ?? data.ReservationUrl ?? "",
          menuUrl: data.menuUrl ?? data.MenuUrl ?? "",
        });
      } catch {
        setMarketingSettings({ reservationUrl: "", menuUrl: "" });
      }
    };

    loadMarketingSettings();
  }, [CompID]);

  useEffect(() => {
    if (!isEdit) {
      setLoading(false);
      return;
    }

    if (!emailPresetId || !CompID) return;

    const loadPreset = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          APIs.GET_EMAIL_PRESET_BY_ID(emailPresetId),
          { params: { CompID } }
        );

        if (!response.data?.status) {
          showErrorToast(response.data?.message || "Email preset not found.");
          navigate("/marketing/manageEmailPresets");
          return;
        }

        const preset = normalizePreset(
          response.data?.data ?? response.data?.Data ?? null
        );

        if (!preset.emailPreset_ID) {
          showErrorToast("Email preset not found.");
          navigate("/marketing/manageEmailPresets");
          return;
        }

        setForm({
          presetTitle: preset.presetTitle,
          subject: preset.subject,
          htmlBody: preset.htmlBody,
          heroImageUrl: preset.heroImageUrl,
          ctaButtonType: normalizeCtaButtonType(preset),
        });
        if (preset.emailPreset_Guid) {
          setEmailPresetGuid(preset.emailPreset_Guid);
        }
        setIsActive(preset.isActive);
      } catch {
        showErrorToast("Failed to load email preset.");
        navigate("/marketing/manageEmailPresets");
      } finally {
        setLoading(false);
      }
    };

    loadPreset();
  }, [isEdit, emailPresetId, CompID, navigate]);

  useEffect(() => {
    if (editorMode !== "visual") return;

    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;

    const root = editor.root;
    const handleQuillFocus = () => setActiveInsertField("body");
    root.addEventListener("focus", handleQuillFocus, true);

    return () => root.removeEventListener("focus", handleQuillFocus, true);
  }, [editorMode, loading]);

  const previewPlaceholders = useMemo(() => {
    const menuSample =
      marketingSettings.menuUrl?.trim() || "https://example.com/restaurant";

    return placeholders.map((p) => {
      const key = p.key ?? p.Key;
      if (key === "MenuUrl") {
        return { ...p, sampleValue: menuSample, SampleValue: menuSample };
      }
      return p;
    });
  }, [placeholders, marketingSettings]);

  const localPreviewSubject = useMemo(
    () => resolveMessageWithSamples(form.subject, previewPlaceholders),
    [form.subject, previewPlaceholders]
  );

  const handleInsertBodyToken = (token) => {
    if (editorMode === "source") {
      setForm((prev) => ({
        ...prev,
        htmlBody: insertTokenAtCursor(htmlSourceRef.current, prev.htmlBody, token),
      }));
      return;
    }

    const editor = quillRef.current?.getEditor?.();
    if (!editor) {
      setForm((prev) => ({
        ...prev,
        htmlBody: `${prev.htmlBody || ""}${token}`,
      }));
      return;
    }

    const range = editor.getSelection(true);
    const index = range?.index ?? editor.getLength();
    editor.insertText(index, token);
    editor.setSelection(index + token.length);
    setForm((prev) => ({ ...prev, htmlBody: editor.root.innerHTML }));
  };

  const handleInsertToken = (token) => {
    if (activeInsertField === "subject") {
      setForm((prev) => ({
        ...prev,
        subject: insertTokenAtCursor(subjectRef.current, prev.subject, token),
      }));
      return;
    }
    handleInsertBodyToken(token);
  };

  const handleMediaSelect = (filePath) => {
    const url = resolveMediaUrl(filePath);
    if (!url) return;

    if (editorMode === "source") {
      const img = `<img src="${url}" alt="" />`;
      setForm((prev) => ({
        ...prev,
        htmlBody: insertTokenAtCursor(htmlSourceRef.current, prev.htmlBody, img),
      }));
      return;
    }

    const editor = quillRef.current?.getEditor?.();
    if (!editor) {
      setForm((prev) => ({
        ...prev,
        htmlBody: `${prev.htmlBody || ""}<img src="${url}" alt="" />`,
      }));
      return;
    }

    const range = editor.getSelection(true);
    const index = range?.index ?? editor.getLength();
    editor.insertEmbed(index, "image", url);
    editor.setSelection(index + 1);
    setForm((prev) => ({ ...prev, htmlBody: editor.root.innerHTML }));
  };

  const refreshServerPreview = useCallback(async () => {
    if (!CompID) return;

    if (!form.subject.trim() && !form.htmlBody.trim()) {
      setPreviewSubject("");
      setPreviewHtml("");
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await axiosInstance.post(APIs.POST_PREVIEW_EMAIL_PRESET, {
        subject: form.subject,
        htmlBody: form.htmlBody,
        heroImageUrl: form.heroImageUrl?.trim() || null,
        ctaButtonType: form.ctaButtonType || EMAIL_PRESET_CTA_NONE,
        compID: Number(CompID),
      });
      const data = response.data?.data ?? response.data?.Data ?? {};
      setPreviewSubject(
        data.resolvedSubject ?? data.ResolvedSubject ?? localPreviewSubject
      );
      setPreviewHtml(data.resolvedHtmlBody ?? data.ResolvedHtmlBody ?? "");
    } catch {
      setPreviewSubject(localPreviewSubject);
      setPreviewHtml("");
    } finally {
      setPreviewLoading(false);
    }
  }, [
    CompID,
    form.subject,
    form.htmlBody,
    form.heroImageUrl,
    form.ctaButtonType,
    localPreviewSubject,
  ]);

  const handleOpenPreview = async () => {
    setPreviewModalOpen(true);
    await refreshServerPreview();
  };

  const handleSave = async () => {
    const title = form.presetTitle?.trim();
    const subject = form.subject?.trim();
    const htmlBody = form.htmlBody?.trim();

    if (!title) {
      showErrorToast("Please provide a preset title.");
      return;
    }
    if (!subject) {
      showErrorToast("Please provide a subject.");
      return;
    }
    if (!htmlBody) {
      showErrorToast("Please provide email content.");
      return;
    }
    if (!CompID || Number(CompID) <= 0) {
      showErrorToast("Company context is missing. Please sign in again.");
      return;
    }

    let resolvedGuid = emailPresetGuid;
    if (!resolvedGuid) {
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        resolvedGuid = crypto.randomUUID();
      } else {
        showErrorToast("Could not generate a preset identifier. Please try again.");
        return;
      }
    }

    const payload = {
      emailPreset_Guid: resolvedGuid,
      compID: Number(CompID),
      presetTitle: title,
      subject,
      htmlBody,
      heroImageUrl: form.heroImageUrl?.trim() || null,
      ctaButtonType: form.ctaButtonType || EMAIL_PRESET_CTA_NONE,
      isActive,
    };

    try {
      setSaving(true);
      const response = await axiosInstance.post(
        APIs.POST_ADD_UPDATE_EMAIL_PRESET,
        payload
      );

      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Could not save email preset.");
        return;
      }

      showSuccessToast(
        response.data?.message ||
          (isEdit
            ? "Email preset updated successfully."
            : "Email preset created successfully.")
      );
      navigate("/marketing/manageEmailPresets");
    } catch (err) {
      showErrorToast(
        err.response?.data?.message ||
          "Something went wrong while saving the email preset."
      );
    } finally {
      setSaving(false);
    }
  };

  const textareaBorder = isDarkMode ? "#6b7280" : "#d1d5db";
  const panelInnerStyle = {
    backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
    border: isDarkMode ? "1px solid #404040" : undefined,
    color: isDarkMode ? "#ffffff" : undefined,
  };

  const activeInsertLabel =
    activeInsertField === "subject" ? "Subject" : "Email content";

  const placeholderChips = (
    <div className="email-preset-sidebar__placeholders">
      {placeholders.map((p) => {
        const token = p.token ?? p.Token;
        const label = p.label ?? p.Label ?? token;
        return (
          <button
            key={p.key ?? p.Key ?? token}
            type="button"
            className="email-preset-placeholder-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleInsertToken(token)}
          >
            <span className="email-preset-placeholder-btn__label">{label}</span>
            <code className="email-preset-placeholder-btn__token">{token}</code>
          </button>
        );
      })}
    </div>
  );

  const renderSidebar = () => (
    <aside
      className={`email-preset-sidebar ${isDarkMode ? "email-preset-sidebar--dark" : ""}`}
    >
      <div className="email-preset-sidebar__section">
        <h6 className="email-preset-sidebar__title">Hero image</h6>
        <ImageUpload
          imageUrl={form.heroImageUrl}
          setImage={(url) => {
            if (!url) {
              setForm((prev) => ({ ...prev, heroImageUrl: "" }));
              return;
            }
            const resolved =
              url.startsWith("http://") ||
              url.startsWith("https://") ||
              url.startsWith("data:")
                ? url
                : resolveMediaUrl(url) || url;
            setForm((prev) => ({ ...prev, heroImageUrl: resolved }));
          }}
          useGrid={false}
          showChangeButton={false}
          enableCrop={false}
          width="100%"
          height="200px"
          uploadText="Upload Image"
          mediaLibraryTitle="Hero image library"
          mediaLibrarySubtitle="Choose the main campaign image for this email preset."
          clampPreviewToParent
        />
        <p className="email-preset-sidebar__hint mt-2 mb-0">
          Shown below the restaurant header in every email.
        </p>
      </div>

      <div
        className="email-preset-sidebar__section email-preset-cta"
        style={{ "--email-preset-cta-accent": themeColor }}
      >
        <div className="email-preset-cta__header">
          <h6 className="email-preset-sidebar__title mb-1">Call to action</h6>
          <p className="email-preset-cta__intro">
            Pick one button to place below the email message.
          </p>
        </div>

        <div className="email-preset-cta__list" role="radiogroup" aria-label="Call to action">
          {EMAIL_PRESET_CTA_OPTIONS.map((option) => {
            const isSelected = form.ctaButtonType === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`email-preset-cta__card${
                  isSelected ? " email-preset-cta__card--active" : ""
                }`}
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    ctaButtonType: option.value,
                  }))
                }
              >
                <span className="email-preset-cta__icon" aria-hidden="true">
                  <i className={`fa ${option.icon}`} />
                </span>
                <span className="email-preset-cta__content">
                  <span className="email-preset-cta__label">{option.label}</span>
                  <span className="email-preset-cta__hint">{option.hint}</span>
                </span>
                <span className="email-preset-cta__indicator" aria-hidden="true">
                  <i className={`fa ${isSelected ? "fa-check-circle" : "fa-circle-o"}`} />
                </span>
              </button>
            );
          })}
        </div>

        {form.ctaButtonType === EMAIL_PRESET_CTA_BOOK_NOW && (
          <div className="email-preset-cta__link-note">
            <span className="email-preset-cta__link-label">Reservation URL</span>
            <span
              className="email-preset-cta__link-value"
              title={marketingSettings.reservationUrl || "Not set in Marketing Settings"}
            >
              {marketingSettings.reservationUrl?.trim() || "Not configured yet"}
            </span>
          </div>
        )}

        {form.ctaButtonType === EMAIL_PRESET_CTA_ORDER_NOW && (
          <div className="email-preset-cta__link-note">
            <span className="email-preset-cta__link-label">Menu URL</span>
            <span
              className="email-preset-cta__link-value"
              title={marketingSettings.menuUrl || "Not set in Marketing Settings"}
            >
              {marketingSettings.menuUrl?.trim() || "Not configured yet"}
            </span>
          </div>
        )}
      </div>

      <div className="email-preset-sidebar__section">
        <div className="email-preset-sidebar__heading">
          <h6 className="email-preset-sidebar__title mb-0">Dynamic fields</h6>
          <span
            className="email-preset-sidebar__target"
            style={{ borderColor: themeColor, color: themeColor }}
          >
            {activeInsertLabel}
          </span>
        </div>
        <p className="email-preset-sidebar__hint">
          Focus Subject or Email content, then click a field to insert.
        </p>
        {placeholderChips}
      </div>
    </aside>
  );

  const gradientBtnStyle = getThemeGradientButtonStyle(themeColor);
  const pageTitle = isEdit ? "Edit Email Preset" : "Create Email Preset";
  const breadcrumbActive = isEdit ? "Edit Email Preset" : "Add Email Preset";

  return (
    <div>
      <MediaLibraryModal
        isOpen={mediaLibraryOpen}
        onClose={() => setMediaLibraryOpen(false)}
        onSelect={handleMediaSelect}
        title="Insert image"
        subtitle="Choose an image from your company media library."
        enableCrop={false}
      />

      <CustomModal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        title="Email preview"
        showButton={false}
        isCentered={true}
        dialogMaxWidth="720px"
        maxHeight="92vh"
        minHeight="auto"
        bodyOverflow="hidden"
      >
        <div className="d-flex justify-content-end mb-2">
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={refreshServerPreview}
            disabled={previewLoading}
            style={{ color: themeColor, textDecoration: "none" }}
          >
            {previewLoading ? "Refreshing…" : "Refresh preview"}
          </button>
        </div>
        <p
          className="mb-2"
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: isDarkMode ? "#e9ecef" : "#212529",
          }}
        >
          Subject: {previewSubject || "(no subject)"}
        </p>
        <iframe
          title="Email preset preview"
          className="email-preset-preview-iframe email-preset-preview-iframe--modal"
          sandbox=""
          srcDoc={buildPreviewDocument(previewHtml)}
        />
      </CustomModal>

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/marketing/manageEmailPresets">Email Preset</Link>
            </li>
            <li className="breadcrumb-item active">{breadcrumbActive}</li>
          </ol>
          <h1 className="page-header mb-0">{pageTitle}</h1>
        </div>

        <div className="ms-auto">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="btn px-4"
            style={gradientBtnStyle}
            onMouseEnter={(e) => onThemeGradientButtonEnter(e, themeColor)}
            onMouseLeave={(e) => onThemeGradientButtonLeave(e, themeColor)}
          >
            <i className="fa fa-save fa-lg me-2 ms-n2 text-white" />
            Submit
          </button>
        </div>
      </div>

      <GridSectionLoader loading={loading || saving} minHeight="400px">
        <div className="card border-0 m-3">
          <div className="row g-4 email-preset-form-layout">
            <div className="col-lg-8">
              <Panel className="mb-0">
                <PanelHeader>General</PanelHeader>
                <PanelBody>
                  <div className="p-4 rounded-3 shadow-sm" style={panelInnerStyle}>
                    <div className="mb-3">
                      <FormInput
                        label="Preset Title"
                        placeHolder="Enter preset title"
                        type="text"
                        value={form.presetTitle}
                        onchange={(e) =>
                          setForm((prev) => ({ ...prev, presetTitle: e.target.value }))
                        }
                      />
                    </div>

                    <div>
                      <label
                        className="form-label"
                        style={{ color: isDarkMode ? "#ffffff" : "#000000" }}
                      >
                        Subject
                      </label>
                      <input
                        ref={subjectRef}
                        type="text"
                        className="form-control"
                        placeholder="Email subject line"
                        value={form.subject}
                        onFocus={() => setActiveInsertField("subject")}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, subject: e.target.value }))
                        }
                        style={{
                          borderRadius: "10px",
                          border: `1px solid ${textareaBorder}`,
                          background: isDarkMode ? "#2d3436" : "#ffffff",
                          color: isDarkMode ? "#ffffff" : undefined,
                        }}
                      />
                    </div>
                  </div>
                </PanelBody>
              </Panel>

              <Panel className="mt-3 mb-0 email-preset-content-panel">
                <PanelHeader>Email content</PanelHeader>
                <PanelBody>
                  <div
                    className="p-4 rounded-3 shadow-sm email-preset-content-panel__inner"
                    style={panelInnerStyle}
                  >
                    <p
                      className="small mb-3"
                      style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
                    >
                      Write the headline and message only. The restaurant header, hero
                      image, and optional call-to-action button are added automatically.
                    </p>
                    <div className="mb-2 d-flex justify-content-between align-items-center">
                      <label
                        className="form-label mb-0"
                        style={{ color: isDarkMode ? "#ffffff" : "#6c757d" }}
                      >
                        Message
                      </label>
                      <div
                        className="btn-group btn-group-sm email-preset-editor-mode-toggle"
                        role="group"
                        aria-label="HTML body editor mode"
                      >
                        <button
                          type="button"
                          className={`btn ${editorMode === "visual" ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setEditorMode("visual")}
                          title="Visual editor"
                          aria-label="Visual editor"
                          aria-pressed={editorMode === "visual"}
                        >
                          <i className="fa fa-font" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className={`btn ${editorMode === "source" ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setEditorMode("source")}
                          title="HTML source"
                          aria-label="HTML source"
                          aria-pressed={editorMode === "source"}
                        >
                          <i className="fa fa-code" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleOpenPreview}
                          title="Preview email"
                          aria-label="Preview email"
                        >
                          <i className="fa fa-eye" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    {editorMode === "visual" ? (
                      <div
                        className={`email-preset-editor-wrap service-area-rich-editor-wrap ${isDarkMode ? "dark" : ""}`}
                      >
                        <ReactQuill
                          ref={quillRef}
                          theme="snow"
                          value={form.htmlBody || ""}
                          onChange={(value) =>
                            setForm((prev) => ({ ...prev, htmlBody: value }))
                          }
                          modules={quillModules}
                          placeholder="Compose email HTML…"
                          className="service-area-rich-editor"
                        />
                      </div>
                    ) : (
                      <textarea
                        ref={htmlSourceRef}
                        className="form-control email-preset-source-textarea"
                        rows={22}
                        value={form.htmlBody}
                        onFocus={() => setActiveInsertField("body")}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, htmlBody: e.target.value }))
                        }
                        placeholder="Paste HTML including &lt;style&gt; blocks and markup"
                        style={{
                          borderRadius: "10px",
                          border: `1px solid ${textareaBorder}`,
                          background: isDarkMode ? "#2d3436" : "#ffffff",
                          color: isDarkMode ? "#ffffff" : undefined,
                        }}
                      />
                    )}
                  </div>
                </PanelBody>
              </Panel>
            </div>

            <div className="col-lg-4">{renderSidebar()}</div>
          </div>
        </div>
      </GridSectionLoader>
    </div>
  );
};

export default EmailPresetFormPage;
